import moment from 'moment-timezone';
import { useApplicationData } from 'utils/useApplicationData';

export enum SupportedState {
  AL = 'AL',
  GA = 'GA',
  KY = 'KY',
  LA = 'LA',
  MO = 'MO',
  MS = 'MS',
  SC = 'SC',
  TX = 'TX',
  TN = 'TN',
  VA = 'VA',
  OH = 'OH',
  FL = 'FL',
  NC = 'NC',
  IN = 'IN',
}

const getTilaBoxValuesByState = (application: ReturnType<typeof useApplicationData>['application']) => {
  const state = application?.variables.borrower_state_or_province as SupportedState;

  if (!application || !state) {
    return null;
  }

  const { variables } = application;

  switch (state) {
    case SupportedState.AL:
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: variables.surcharge,
        prepaidFinanceCharges: variables.surcharge,
        amountFinanced: variables.amount_financed,
      };
    case SupportedState.GA:
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: variables.points,
        prepaidFinanceCharges: variables.points,
        amountFinanced: variables.amount_financed,
      };
    case SupportedState.KY:
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: variables.total_other_charges,
        prepaidFinanceCharges: variables.total_other_charges,
        amountFinanced: variables.amount_financed,
      };
    case SupportedState.LA:
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: 70,
        prepaidFinanceCharges: 70,
        amountFinanced: variables.amount_financed,
      };
    case SupportedState.MO:
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: variables.loan_origination_fee,
        prepaidFinanceCharges: variables.loan_origination_fee,
        amountFinanced: variables.amount_financed,
      };
    case SupportedState.MS:
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: variables.ms_closing_fee,
        prepaidFinanceCharges: variables.ms_closing_fee,
        amountFinanced: variables.amount_financed,
      };
    case SupportedState.NC:
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: variables.total_other_charges,
        prepaidFinanceCharges: variables.total_other_charges,
        amountFinanced: variables.amount_financed,
      };
    case SupportedState.OH: {
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: variables.total_other_charges! + variables.loan_origination_fee!,
        prepaidFinanceCharges: variables.total_other_charges! + variables.loan_origination_fee!,
        amountFinanced: variables.amount_financed,
      };
    }
    case SupportedState.SC:
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: 0,
        prepaidFinanceCharges: 0,
        amountFinanced: variables.amount_financed,
      };
    case SupportedState.TN:
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: variables.prepaid_fees,
        prepaidFinanceCharges: variables.prepaid_fees,
        amountFinanced: variables.amount_financed,
      };
    case SupportedState.VA: {
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: variables.total_other_charges,
        prepaidFinanceCharges: variables.total_other_charges,
        amountFinanced: variables.amount_financed,
      };
    }
    case SupportedState.FL: {
      return {
        amountPaidToYou: variables.amount_financed! - variables.docstamptax77!,
        amountPaidToUs: variables.total_other_charges,
        prepaidFinanceCharges: variables.total_other_charges,
        amountFinanced: variables.amount_financed,
        docStampTax: variables.docstamptax77,
      };
    }
    case SupportedState.TX: {
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: variables.administrative_fee,
        prepaidFinanceCharges: variables.administrative_fee,
        amountFinanced: variables.amount_financed,
        principalBalance: variables.amount_financed! + variables.administrative_fee!,
      };
    }
    case SupportedState.IN: {
      return {
        amountPaidToYou: variables.amount_financed,
        amountPaidToUs: variables.total_other_charges,
        prepaidFinanceCharges: variables.total_other_charges,
        amountFinanced: variables.amount_financed,
      };
    }
    default:
      return null as never;
  }
};

interface TilaBoxValues {
  apr: number;
  financeCharge: number;
  amountFinanced: number;
  loanBalance: number;
  numberOfPaymentsMinusOne: number;
  firstPaymentAmount: number;
  regularPaymentAmount: number;
  currentDatePlus32: string;
}

const getDefaultTilaBoxValues = (
  application: ReturnType<typeof useApplicationData>['application'],
): TilaBoxValues | null => {
  if (!application) {
    return null;
  }

  const currentDatePlus32 = moment(application.variables.first_payment_date!)
    .utcOffset(application.variables.first_payment_date!)
    .format('l');

  return {
    apr: application.variables.apr!,
    financeCharge: application.variables.finance_charge!,
    amountFinanced: application.variables.amount_financed!,
    loanBalance: application.variables.loan_balance!,
    regularPaymentAmount: application.variables.monthly_payment!,
    currentDatePlus32,
    numberOfPaymentsMinusOne: application.variables.number_of_payments! - 1,
    firstPaymentAmount: application.variables.first_payment_amount!,
  };
};

export const useTilaBoxData = () => {
  const { application, applicationLoading, borrowerLoading } = useApplicationData();

  return {
    application,
    applicationLoading,
    borrowerLoading,
    defaultTilaBoxValues: getDefaultTilaBoxValues(application),
    tilaBoxValuesByState: getTilaBoxValuesByState(application),
  };
};
